import { useEffect, useState } from "react";
import { Button, Icon, Panel, SearchInput, Spinner, Typography } from "@/components/atoms";
import { useSiteConfig } from "@/hooks";
import { useLazyVerifyProviderQuery } from "@/redux/apis/auth/authApi";
import { useAppDispatch } from "@/redux/hooks";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { Operator } from "@/redux/slices/operator/types";
import { OperatorEmblem } from "../contents";
import { useSignUpContext } from "../context";

export const ProviderSelectForm = () => {
  const dispatch = useAppDispatch();
  const { apiUrl } = useSiteConfig();
  const { setStep, userTypeData } = useSignUpContext();
  const [verifyProvider, { isFetching }] = useLazyVerifyProviderQuery();
  const [isProviderInvalid, setIsProviderInvalid] = useState<boolean>(false);
  const [selectedOperator, setSelectedOperator] = useState<Operator>();
  const [search, setSearch] = useState<string>();

  useEffect(() => {
    if (search) {
      verifyProvider({ providerCode: search, apiUrl })
        .unwrap()
        .then((operator) => {
          setIsProviderInvalid(false);
          setSelectedOperator(operator);
        })
        .catch(() => {
          setIsProviderInvalid(true);
          setSelectedOperator(undefined);
        });
    } else {
      setIsProviderInvalid(false);
      setSelectedOperator(undefined);
    }
  }, [search, verifyProvider, apiUrl, userTypeData]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedOperator && userTypeData) {
      const { singleTenant } = selectedOperator;

      if (singleTenant) {
        dispatch(setActiveOperator(selectedOperator));
        setStep("signup");
      } else {
        dispatch(setActiveOperator(selectedOperator));
        setStep("signup");
      }
    }
  };

  const onBackClick = () => setStep("user-select");

  const renderProvider = () => {
    if (isFetching) {
      return (
        <div className="flex flex-col items-center justify-center gap-y-4 px-7 pb-[30px] pt-8 md:px-14 md:pt-[60px]">
          <Spinner className="h-12 w-12" />
          <Typography variant="paragraph">Checking Provider Code...</Typography>
        </div>
      );
    }

    if (isProviderInvalid) {
      return (
        <div className="flex flex-col items-center justify-center gap-y-4 px-7 pb-[30px] pt-8 md:px-14 md:pt-[60px]">
          <Icon variant="Bold" name="Danger" className="text-warning" />
          <Typography variant="paragraph" className="text-center">
            We couldn&apos;t find the Transport Provider Code that you&apos;ve entered. Please contact your provider
          </Typography>
        </div>
      );
    } else if (selectedOperator) {
      return (
        <div className="flex flex-col items-center justify-center gap-y-4 px-7 pb-[30px] pt-8 md:px-14 md:pt-[60px]">
          <OperatorEmblem name={selectedOperator.name} emblem={selectedOperator.images.emblem} />
          <Typography variant="action" className="text-center">
            {selectedOperator.name}
          </Typography>
        </div>
      );
    }
  };

  const renderButtons = () => (
    <div className="mt-6 flex flex-col gap-4 md:flex-row md:justify-end">
      <div className="hidden md:block">
        <Button variant="secondary" startIcon="ArrowLeft" size="lg" onClick={onBackClick}>
          Back
        </Button>
      </div>
      <Button variant="primary" type="submit" endIcon="ArrowRight" size="lg" disabled={!selectedOperator} className="w-full md:w-auto">
        Next
      </Button>
      <Button variant="secondary" startIcon="ArrowLeft" size="lg" onClick={() => setStep("user-select")} className="w-full md:hidden">
        Back
      </Button>
    </div>
  );

  return (
    <form onSubmit={onSubmit} className="w-full max-w-[510px]">
      <Panel>
        <div className="mb-7 flex flex-col gap-y-1.5">
          <Typography variant="h1">Sign up</Typography>
          <Typography variant="paragraph">Join your preferred operator</Typography>
        </div>
        <div className="flex flex-col">
          <Typography variant="h3">Transport Provider Code</Typography>
          <SearchInput
            className="mt-4"
            search={search}
            onSearch={(value) => setSearch(value)}
            placeholder="Enter your Transport Provider Code"
            isLoading={isFetching}
          />
          <Typography variant="small" className="mt-1 text-neutral-dark-gray">
            Use the transport provider code to connect with a specific operator. Simply ask the operator for the code.
          </Typography>
        </div>
        {renderProvider()}
      </Panel>
      {renderButtons()}
    </form>
  );
};
